import classNames from 'classnames';
import { useCallback } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Badge, Button, Flex, Heading } from 'yoso';
import { breakpoints } from '~/components/Breakpoint/Breakpoint.hook';
import { PlaceholderCard } from '~/components/Card/Placeholder';
import { Carousel } from '~/components/Carousel';
import { myCitizenItemsUrl } from '~/components/Dosi/Dosi.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { useEvolutions } from '~/components/Evolve/Evolve.hooks';
import { FriendNftContent } from '~/components/Friend/Content/FriendNftContent';
import friendNftContentStyles from '~/components/Friend/Content/FriendNftContent.module.scss';
import { T } from '~/components/I18n';
import { Boost2D } from '~/components/Icon';
import { formatBoost } from '~/components/Util/number';
import styles from './EvolvedFriends.module.scss';

export const carouselProps = {
  navigation: true,
  pagination: {
    dynamicBullets: true,
  },
  breakpoints: {
    0: {
      slidesPerView: 2,
      slidesPerGroup: 2,
      spaceBetween: 11,
    },
    [breakpoints.small]: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 11,
    },
    [breakpoints.medium]: {
      slidesPerView: 4,
      slidesPerGroup: 4,
      spaceBetween: 20,
    },
  },
};

export type EvolvedFriendProps = {
  className?: string;
};

export const EvolvedFriends = ({ className }: EvolvedFriendProps) => {
  const { data: { completed = [] } = {}, isFetchedAfterMount } = useEvolutions();
  const { open } = useEmbeddedViewer();

  const handleSeeAllClick = useCallback(() => {
    open(myCitizenItemsUrl);
  }, [open]);

  if (completed.length === 0 && isFetchedAfterMount) {
    throw new BaseError('EMPTY');
  }

  return (
    <div className={classNames(styles.root, className)}>
      <Flex container justifyContent="space-between" alignItems="center" className={styles.header}>
        <Heading level={4} className={styles.title}>
          <T name="citizen_evolve_completed" />
        </Heading>
        <Button className={styles.button} color="default" size="small" onClick={handleSeeAllClick}>
          <T name="citizen_button_title_seeall" pre="nowrap" />
        </Button>
      </Flex>
      <div>
        <Carousel
          {...carouselProps}
          className={styles.carousel}
          placeholder={<PlaceholderCard type="face" />}>
          {completed.map((friend) => (
            <SwiperSlide key={friend.nftItemId}>
              <a href={`${import.meta.env.VITE_CITIZEN_STORE_URL}/nfts/${friend.nftItemId}`}>
                <FriendNftContent
                  {...friend}
                  contentsUrl={friend.contentsUrl}
                  originContentsUrl={friend.contentsUrl}
                  badge={
                    <Badge className={classNames(friendNftContentStyles.badge, styles.badge)}>
                      <Boost2D />
                      Boost {formatBoost(friend.boost)}%
                    </Badge>
                  }
                  title={friend.growth}
                />
              </a>
            </SwiperSlide>
          ))}
        </Carousel>
      </div>
    </div>
  );
};
